import { useRef, useState } from 'react'
import Select from 'react-select'
import { X } from 'lucide-react'
import { Button, FormField } from '../common'
import { ActivityIndicator } from '../common/feedback/ActivityIndicator'
import { REACT_SELECT_STYLES } from '../common/reactSelectStyles'
import { AI_IMAGE_SIZE_OPTIONS, DEFAULT_AI_IMAGE_SIZE } from './imagePrompt'
import { AI_REFERENCE_ACCEPT, MAX_AI_REFERENCE_IMAGES } from './useReferenceImages'
import { useReferenceImageRetry } from './useReferenceImageRetry'
import { canResumeImageGenerationJob } from './imageGenerationJob'
import styles from './MediaSection.module.css'

export default function ImagePromptComposer({
  imagePrompt,
  imagePromptFields,
  onImagePromptChange,
  onImagePromptFieldChange,
  onBuildPromptFromFields,
  imagePromptPresets = [],
  selectedPromptPresetId = '',
  onPromptPresetChange,
  onSavePromptPreset,
  onSaveDefaultPromptPreset,
  promptPresetSaving = false,
  imagePending = false,
  imageStatus = null,
  onGenerateImage,
  onDiscardImage,
  aiCapabilities = {},
  generateLabel = 'Generate image',
  generatingLabel = 'Generating...',
  framed = true,
  referenceImages = [],
  onAddReferenceImages,
  onRemoveReferenceImage,
  referenceImagesUploading = false,
  maxReferenceImages = MAX_AI_REFERENCE_IMAGES,
}) {
  const [imageSize, setImageSize] = useState(DEFAULT_AI_IMAGE_SIZE)
  const referenceInputRef = useRef(null)
  // The server deletes a reference 7 days after upload, so a draft can outlive
  // its images. A stored image that fails to load is retried before its tile
  // is shown as expired.
  const referenceImageLoads = useReferenceImageRetry(referenceImages)
  const imageGenerationEnabled = Boolean(aiCapabilities.features?.image_generation?.enabled)
  const hasPrompt = Boolean(imagePrompt?.trim())
  const hasGuidedPrompt = Object.values(imagePromptFields || {}).some((value) => String(value || '').trim())
  const canSavePrompt = hasPrompt || hasGuidedPrompt
  const showPromptControls = Boolean(
    imagePromptPresets.length
      || onPromptPresetChange
      || onSavePromptPreset
      || onSaveDefaultPromptPreset
  )
  const showQuestionnaire = Boolean(onImagePromptFieldChange)
  const resumable = canResumeImageGenerationJob(imageStatus)
  const paused = resumable && !imagePending
  const activityStatus = paused ? 'paused' : imageStatus?.status || (imagePending ? 'processing' : null)
  const activityLabel = {
    paused: 'Image status unavailable',
    pending: 'Image queued',
    processing: 'Generating image',
    completed: 'Image ready for review',
    failed: 'Image generation failed',
  }[activityStatus]
  // Reference images render only for hosts that opt in.
  const showReferenceImages = Boolean(onAddReferenceImages)
  const referenceImagesFull = referenceImages.length >= maxReferenceImages
  const hasExpiredReference = referenceImages.some(
    (reference) => referenceImageLoads.imageState(reference).phase === 'expired',
  )

  const handleReferenceSelection = (event) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (files.length) onAddReferenceImages?.(files)
  }

  const referenceButton = showReferenceImages ? (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={() => referenceInputRef.current?.click()}
      disabled={!imageGenerationEnabled || imagePending || referenceImagesUploading || referenceImagesFull}
    >
      Upload reference image
    </Button>
  ) : null

  return (
    <div className={framed ? styles.panel : styles.promptComposerBare}>
      <FormField
        label="Prompt"
        name="image-prompt"
        type="textarea"
        rows={7}
        value={imagePrompt}
        onChange={(event) => onImagePromptChange?.(event.target.value)}
        placeholder="Describe the poster, subjects, layout, stats, and exact text to include..."
      />
      {showPromptControls ? (
        <div className={styles.aiPresetRow}>
          <label className={styles.aiPresetSelectWrap}>
            <span>Prompt preset</span>
            <select
              value={selectedPromptPresetId}
              onChange={(event) => onPromptPresetChange?.(event.target.value)}
              className={styles.aiPresetSelect}
              disabled={!onPromptPresetChange}
            >
              <option value="">Custom direction</option>
              {imagePromptPresets.map((preset) => (
                <option key={preset.id} value={preset.id}>{preset.name}</option>
              ))}
            </select>
          </label>
          <div className={styles.aiPresetActions}>
            {onSavePromptPreset ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onSavePromptPreset?.()}
                disabled={promptPresetSaving || !canSavePrompt}
              >
                Save prompt
              </Button>
            ) : null}
            {onSaveDefaultPromptPreset ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onSaveDefaultPromptPreset?.()}
                disabled={promptPresetSaving || !canSavePrompt}
              >
                Save default
              </Button>
            ) : null}
            {referenceButton}
          </div>
        </div>
      ) : null}
      {showReferenceImages ? (
        <>
          <input
            ref={referenceInputRef}
            type="file"
            accept={AI_REFERENCE_ACCEPT}
            multiple
            onChange={handleReferenceSelection}
            aria-label="Upload reference image"
            className={styles.hiddenFileInput}
            tabIndex={-1}
          />
          {!showPromptControls ? (
            <div className={styles.referenceActions}>{referenceButton}</div>
          ) : null}
          {referenceImages.length ? (
            <div className={styles.referenceImages}>
              <ul className={styles.referenceList} role="list" aria-label="Reference images">
                {referenceImages.map((reference, index) => {
                  const number = index + 1
                  const uploading = reference.status === 'uploading'
                  const imageLoad = referenceImageLoads.imageState(reference)
                  const expired = imageLoad.phase === 'expired'
                  const retrying = imageLoad.phase === 'retrying'
                  return (
                    <li
                      key={reference.key || reference.id || index}
                      className={expired ? `${styles.referenceTile} ${styles.referenceTileExpired}` : styles.referenceTile}
                    >
                      <span className={styles.referenceNumber} aria-hidden="true">{number}</span>
                      {expired ? (
                        <span className={styles.referenceExpired}>Expired – upload again</span>
                      ) : reference.url && !retrying ? (
                        <img
                          // A new attempt is a new element, so the browser requests it again.
                          key={imageLoad.attempt}
                          src={imageLoad.src}
                          alt={`Reference image ${number}${reference.title ? `: ${reference.title}` : ''}`}
                          className={styles.referenceImage}
                          onError={() => referenceImageLoads.handleError(reference)}
                          onLoad={() => referenceImageLoads.handleLoad(reference)}
                        />
                      ) : null}
                      {uploading ? (
                        <span className={styles.referenceUploading}>Uploading...</span>
                      ) : null}
                      {retrying ? (
                        <span className={styles.referenceUploading}>Loading…</span>
                      ) : null}
                      {uploading ? null : (
                        <button
                          type="button"
                          className={styles.referenceRemove}
                          onClick={() => onRemoveReferenceImage?.(reference.id)}
                          disabled={imagePending}
                          aria-label={`Remove reference image ${number}`}
                        >
                          <span className={styles.referenceRemoveIcon}>
                            <X size={14} />
                          </span>
                        </button>
                      )}
                    </li>
                  )
                })}
              </ul>
              <p className={styles.panelHelper}>
                {hasExpiredReference
                  ? 'Reference images are deleted 7 days after upload. Remove the expired ones and upload them again.'
                  : referenceImagesFull
                    ? `${maxReferenceImages} of ${maxReferenceImages} reference images added.`
                    : `Up to ${maxReferenceImages} images (PNG, JPEG or WebP). Refer to them in the prompt as image 1, image 2, and so on.`}
              </p>
            </div>
          ) : null}
        </>
      ) : null}
      {showQuestionnaire ? (
        <details className={styles.promptQuestionnaire}>
          <summary className={styles.promptQuestionnaireSummary}>
            Develop prompt with guided questions
          </summary>
          <div className={styles.promptQuestionnaireBody}>
            <div className={styles.guidedPromptGrid}>
              <FormField
                label="Title / theme"
                value={imagePromptFields?.titleTheme || ''}
                onChange={(event) => onImagePromptFieldChange?.('titleTheme', event.target.value)}
              />
              <FormField
                label="Overall feel"
                value={imagePromptFields?.overallFeel || ''}
                onChange={(event) => onImagePromptFieldChange?.('overallFeel', event.target.value)}
              />
              <FormField
                label="Stats"
                type="textarea"
                rows={3}
                value={imagePromptFields?.stats || ''}
                onChange={(event) => onImagePromptFieldChange?.('stats', event.target.value)}
              />
              <FormField
                label="Layout / size"
                type="textarea"
                rows={3}
                value={imagePromptFields?.layoutSize || ''}
                onChange={(event) => onImagePromptFieldChange?.('layoutSize', event.target.value)}
              />
              <FormField
                label="Images / subjects"
                type="textarea"
                rows={3}
                value={imagePromptFields?.images || ''}
                onChange={(event) => onImagePromptFieldChange?.('images', event.target.value)}
              />
              <FormField
                label="Advanced instructions"
                type="textarea"
                rows={3}
                value={imagePromptFields?.advanced || ''}
                onChange={(event) => onImagePromptFieldChange?.('advanced', event.target.value)}
              />
            </div>
            <div className={styles.questionnaireActions}>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onBuildPromptFromFields?.()}
                disabled={!hasGuidedPrompt}
              >
                Use as prompt
              </Button>
            </div>
          </div>
        </details>
      ) : null}
      <div className={styles.imageSizeField}>
        <label htmlFor="image-size">Image size</label>
        <Select
          inputId="image-size"
          classNamePrefix="image-size-select"
          options={AI_IMAGE_SIZE_OPTIONS}
          value={AI_IMAGE_SIZE_OPTIONS.find((option) => option.value === imageSize)}
          onChange={(option) => setImageSize(option?.value || DEFAULT_AI_IMAGE_SIZE)}
          isSearchable={false}
          styles={REACT_SELECT_STYLES}
        />
      </div>
      <Button
        type="button"
        className={styles.generateButton}
        onClick={() => onGenerateImage?.(imageSize)}
        disabled={imagePending || (!resumable && (!imageGenerationEnabled || referenceImagesUploading || !hasPrompt))}
      >
        {imagePending ? generatingLabel : resumable ? 'Resume image' : generateLabel}
      </Button>
      {paused && onDiscardImage ? (
        <div className={styles.referenceActions}>
          <Button type="button" variant="secondary" onClick={onDiscardImage}>
            Start over
          </Button>
          <p className={styles.panelHelper}>
            The previous image may still finish. Start over to generate a new image.
          </p>
        </div>
      ) : null}
      {activityStatus ? (
        <ActivityIndicator
          compact={activityStatus === 'completed' || activityStatus === 'failed' || paused}
          surface={activityStatus === 'pending' || activityStatus === 'processing'}
          status={activityStatus === 'completed' ? 'success' : (activityStatus === 'failed' ? 'error' : activityStatus)}
          label={activityLabel}
          detail={paused ? (imageStatus?.error || 'Resume to check your existing image.') : activityStatus === 'failed'
            ? (imageStatus?.error || 'Retry when ready.')
            : activityStatus === 'pending' ? 'Waiting for image generation to start.' : 'Preparing a review asset.'}
          startedAt={imageStatus?.startedAt}
        />
      ) : null}
      {!imageGenerationEnabled ? (
        <p className={styles.panelHelper}>
          {aiCapabilities.features?.image_generation?.reason?.message || 'AI image generation is unavailable.'}
        </p>
      ) : null}
    </div>
  )
}
